import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import type {IJSONProvider} from "data/providers/json/json.provider";
import {Bindings} from "data/constants/bindings";
import {RoundStatus, TournamentStatus} from "data/enums";
import {first, isEqual, last} from "lodash";
import type {IPickResponse} from "data/providers/api/pinpoint.api.provider";
import type {IPinpointStore} from "data/stores/pinpoint/pinpoint.store";

export interface ITournament {
	id: number | null;
	feedCode: string | null;
	status: TournamentStatus;
	teeTime: string;
	isPostponed: boolean;
	golfers: number[];
}

export interface IGolfers {
	usa: number[];
	europe: number[];
}

export interface ISession {
	id: number | null;
	name: string;
	pinpointStatus: RoundStatus;
	startDate: string;
	endDate: string;
	longestDriveHole: string | null;
	nearestPinHole: string | null;
	golfers: IGolfers[];
}

export interface ISessionWithPick extends ISession {
	pick: IPickResponse | null;
	parentId: number | null;
}

export interface IRound {
	id: number | null;
	name: string;
	status: RoundStatus;
	date: string;
	tiebreakerPoints: number;
	sessions: ISession[];
}

export interface IRoundsStore {
	get getIsLoading(): boolean;

	get list(): IRound[];

	get scheduleRounds(): IRound[];
	get completedRounds(): IRound[];

	get nextUpSessions(): ISession[];
	get upcomingSessions(): ISession[];
	get liveSessions(): ISession[];
	get editSessions(): ISession[];
	get completedSessions(): ISession[];
	get notLiveOrCompletedSessions(): ISession[];

	get currentRound(): IRound | undefined;

	get scoreRound(): IRound | null;

	getRoundById(roundId: number): ISessionWithPick | null;
	fetchRounds(): Promise<void>;
}

@injectable()
export class RoundsStore implements IRoundsStore {
	@observable private _isLoading: boolean = false;

	constructor(
		@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider,
		@inject(Bindings.PinpointStore) private _pinpointStore: IPinpointStore
	) {
		makeAutoObservable(this);
	}

	@observable private _list: IRound[] = [];

	get list() {
		return this._list;
	}

	get sessions() {
		return this.list.reduce((acc, round) => {
			const sessionsWithPick = round.sessions.map((session) => ({
				...session,
				pick: this._pinpointStore.getPickByRoundId(session.id!),
				parentId: round.id,
			}));

			return [...acc, ...sessionsWithPick];
		}, [] as ISessionWithPick[]);
	}

	getRoundById(roundId: number) {
		return this.sessions.find((session) => isEqual(session.id, roundId)) || null;
	}

	get scheduleRounds() {
		return this._list.filter((e) => e.status === RoundStatus.Scheduled);
	}

	get completedRounds() {
		return this._list.filter((e) => e.status === RoundStatus.Complete);
	}

	get getIsLoading(): boolean {
		return this._isLoading;
	}

	get currentRound() {
		return this.activeRound || first(this.scheduleRounds) || last(this.list);
	}

	get scoreRound() {
		return this.activeRound || last(this.completedRounds) || null;
	}

	private get activeRound() {
		return this.list.find((e) => e.status === RoundStatus.Playing);
	}

	get editSessions() {
		return this.sessions.filter(
			(e) => Boolean(e.pick?.id) && e.pinpointStatus === RoundStatus.Scheduled
		);
	}

	get noEditSessions() {
		return this.sessions.filter(
			(e) => !this.editSessions.includes(e) && e.pinpointStatus === RoundStatus.Scheduled
		);
	}

	get nextUpSessions() {
		const firstRound = first(this.noEditSessions);
		return this.noEditSessions.filter((e) => e.parentId === firstRound?.parentId);
	}

	get upcomingSessions() {
		return this.noEditSessions.filter((e) => !this.nextUpSessions.includes(e));
	}

	get liveSessions() {
		return this.sessions.filter((e) => e.pinpointStatus === RoundStatus.Playing);
	}

	get completedSessions() {
		return this.sessions.filter((e) => e.pinpointStatus === RoundStatus.Complete);
	}

	get notLiveOrCompletedSessions() {
		return this.sessions.filter(
			(e) => ![RoundStatus.Playing, RoundStatus.Complete].includes(e.pinpointStatus)
		);
	}

	@action
	async fetchRounds() {
		const {data} = await this._jsonProvider.rounds();

		runInAction(() => {
			this._list = data;
		});
	}
}
